import { useState } from 'react'
import { Button, FormInput } from 'components'
import { DUNGEON_SPRITES, TREASURE_TYPES, spriteImagePath } from '../../games/pinmonkey/config/constants'
import { DialogEditor } from '../../games/pinmonkey/editor/dialog-editor'

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  color: '#999',
  marginBottom: '0.25rem',
  marginTop: '0.75rem',
  textTransform: 'uppercase',
}

const sectionStyle = {
  marginTop: '1.25rem',
  paddingTop: '1rem',
  borderTop: '1px solid #222',
}

const inputStyle = {
  background: '#111',
  border: '1px solid #444',
  color: '#e8d5a0',
  padding: '0.3rem 0.5rem',
  fontFamily: "'Silkscreen', monospace",
  fontSize: '0.8rem',
  boxSizing: 'border-box',
}

const tinyInput = {
  ...inputStyle,
  padding: '0.2rem 0.4rem',
  fontSize: '0.75rem',
}

const cardStyle = {
  padding: '0.5rem',
  border: '1px solid #2a2a2a',
  background: '#0a0a0a',
  marginBottom: '0.5rem',
}

const addBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#888',
  cursor: 'pointer',
  fontFamily: "'Silkscreen', monospace",
  fontSize: '0.75rem',
  padding: '0.2rem 0',
}

const removeBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#c44',
  cursor: 'pointer',
  fontFamily: "'Silkscreen', monospace",
  fontSize: '0.75rem',
  padding: '0 0.2rem',
  flexShrink: 0,
}

const spriteGridStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.4rem',
  marginBottom: '0.75rem',
}

const spriteBtnStyle = (selected) => ({
  width: 48,
  height: 48,
  background: selected ? '#1e1408' : '#0e0c08',
  border: `2px solid ${selected ? '#c8a060' : '#332211'}`,
  padding: 0,
  cursor: 'pointer',
  overflow: 'hidden',
})

// ── Inventory editor ───────────────────────────────────────────────────────

const InventoryEditor = ({ inventory, onChange }) => {
  const updateItem = (i, changes) =>
    onChange(inventory.map((it, idx) => idx === i ? { ...it, ...changes } : it))

  return (
    <div style={sectionStyle}>
      <label style={labelStyle}>Shop Inventory</label>
      <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '0.5rem' }}>
        Items this NPC sells. Enable "Shop" on a dialog to open the shop after that dialog.
      </div>
      {inventory.map((item, i) => (
        <div key={item.id ?? i} style={cardStyle}>
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.25rem', alignItems: 'center' }}>
            <select
              value={item.type || 'gold'}
              onChange={(e) => updateItem(i, { type: e.target.value })}
              style={{ ...tinyInput, flex: 1 }}
            >
              {TREASURE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button
              type="button"
              style={removeBtnStyle}
              onClick={() => onChange(inventory.filter((_, idx) => idx !== i))}
            >
              ✕
            </button>
          </div>
          <input
            value={item.name || ''}
            onChange={(e) => updateItem(i, { name: e.target.value })}
            placeholder="Name"
            style={{ ...tinyInput, width: '100%', marginBottom: '0.25rem' }}
          />
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <input
              type="number"
              min={0}
              value={item.value ?? ''}
              placeholder="Value"
              onChange={(e) => updateItem(i, { value: Number(e.target.value) || 0 })}
              style={{ ...tinyInput, flex: 1 }}
            />
            <input
              type="number"
              min={0}
              value={item.price ?? ''}
              placeholder="Price (g)"
              onChange={(e) => updateItem(i, { price: Number(e.target.value) || 0 })}
              style={{ ...tinyInput, flex: 1 }}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        style={addBtnStyle}
        onClick={() => onChange([...inventory, { id: `item_${Date.now()}`, type: 'gold', name: '', value: 10, price: 20 }])}
      >
        + Add item
      </button>
    </div>
  )
}

// ── Main form ──────────────────────────────────────────────────────────────

export const NpcAdminForm = ({ initial = {}, onSubmit, submitLabel = 'Save' }) => {
  const [state, setState] = useState({
    name:                     '',
    sprite:                   DUNGEON_SPRITES[0],
    background_color:         '#4488cc',
    stationary:               false,
    boundary_period:          '',
    dialogs:                  [],
    inventory:                [],
    ...initial,
    dialogs:                  initial.dialogs                  ?? [],
    inventory:                initial.inventory                ?? [],
    boundary_period:          initial.boundary_period          ?? '',
  })

  const set = (field, value) => setState((prev) => ({ ...prev, [field]: value }))
  const handler = ({ target: { name, value, type, checked } }) =>
    set(name, type === 'checkbox' ? checked : value)

  const handleSubmit = () => {
    onSubmit({
      name:                     state.name,
      sprite:                   state.sprite || null,
      background_color:         state.background_color || null,
      stationary:               state.stationary,
      boundary_period:          state.boundary_period !== '' ? parseInt(state.boundary_period) : null,
      dialogs:                  state.dialogs,
      inventory:                state.inventory,
    })
  }

  return (
    <>
      <FormInput label="Name" name="name" onChange={handler} value={state.name} />

      <label style={labelStyle}>Sprite</label>
      <div style={spriteGridStyle}>
        {DUNGEON_SPRITES.map((sp) => (
          <button
            key={sp}
            type="button"
            title={sp}
            style={spriteBtnStyle(state.sprite === sp)}
            onClick={() => set('sprite', sp)}
          >
            <img
              src={spriteImagePath(sp)}
              alt={sp}
              style={{ width: '100%', height: '100%', imageRendering: 'pixelated', objectFit: 'contain' }}
            />
          </button>
        ))}
      </div>

      <FormInput
        label="Background Color"
        name="background_color"
        onChange={handler}
        value={state.background_color ?? ''}
        placeholder="#4488cc"
      />
      <FormInput
        label="Boundary Period (ms)"
        name="boundary_period"
        onChange={handler}
        value={state.boundary_period ?? ''}
        type="number"
      />

      <label style={{ ...labelStyle, marginTop: '0.75rem' }}>
        <input
          type="checkbox"
          name="stationary"
          checked={!!state.stationary}
          onChange={handler}
          style={{ marginRight: '0.4rem' }}
        />
        Stationary
      </label>

      <DialogEditor
        label="Dialogs"
        dialogs={state.dialogs}
        onChange={(v) => set('dialogs', v)}
      />

      <InventoryEditor
        inventory={state.inventory}
        onChange={(v) => set('inventory', v)}
      />

      <div style={{ marginTop: '1.5rem' }}>
        <Button className="large" onPress={handleSubmit}>{submitLabel}</Button>
      </div>
    </>
  )
}
